/**
 * Veluna Web Vitals Tracking - TILE P2
 * Entity-Led Influence 2026: Speed Gate + Web Vitals SLO
 * 
 * Captures Core Web Vitals (LCP, INP, CLS) and logs to console + telemetry endpoint
 */

(function() {
  'use strict';

  // Check if Web Vitals library is available (would need to be loaded separately)
  // For now, we'll use a lightweight implementation that works with browser APIs
  
  // Configuration
  const TELEMETRY_ENDPOINT = '/api/telemetry/web-vitals';
  const ENABLE_CONSOLE_LOG = true;
  const ENABLE_TELEMETRY = true; // Set to false if backend not available

  // Store metrics
  const metrics = {
    lcp: null,
    inp: null,
    cls: null,
    fcp: null,
    ttfb: null
  };

  // Helper: Send metric to telemetry endpoint
  async function sendMetric(metricName, value, metadata) {
    if (!ENABLE_TELEMETRY) {
      return;
    }

    // Check endpoint availability - gate /api/telemetry/web-vitals
    if (typeof window.velunaEndpointAvailable === 'function') {
      const available = await window.velunaEndpointAvailable(TELEMETRY_ENDPOINT);
      if (!available) {
        // Log once per session
        if (!window.__VELUNA_GATE_LOGGED || !window.__VELUNA_GATE_LOGGED[TELEMETRY_ENDPOINT]) {
          if (!window.__VELUNA_GATE_LOGGED) {
            window.__VELUNA_GATE_LOGGED = {};
          }
          window.__VELUNA_GATE_LOGGED[TELEMETRY_ENDPOINT] = true;
          console.log('[VELUNA_GATE] skip ' + TELEMETRY_ENDPOINT);
        }
        return; // Endpoint not available, skip silently
      }
    } else {
      // Endpoint gate not loaded - log once
      if (!window.__VELUNA_GATE_LOGGED || !window.__VELUNA_GATE_LOGGED['gate_not_loaded']) {
        if (!window.__VELUNA_GATE_LOGGED) {
          window.__VELUNA_GATE_LOGGED = {};
        }
        window.__VELUNA_GATE_LOGGED['gate_not_loaded'] = true;
        console.warn('[VELUNA_GATE] velunaEndpointAvailable not found - endpoint gating disabled');
      }
    }

    try {
      const payload = {
        metric: metricName,
        value: value,
        page_type: 'article',
        url: window.location.href,
        timestamp: new Date().toISOString(),
        ...metadata
      };

      // Use sendBeacon for reliability (doesn't block page unload)
      if (navigator.sendBeacon && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(TELEMETRY_ENDPOINT, blob);
      } else {
        // Fallback to fetch
        fetch(TELEMETRY_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true
        }).catch(function(err) {
          console.warn('[Veluna Web Vitals] Failed to send metric:', err);
        });
      }
    } catch (e) {
      console.warn('[Veluna Web Vitals] Error sending metric:', e);
    }
  }

  // Helper: Log metric to console
  function logMetric(metricName, value, metadata) {
    if (ENABLE_CONSOLE_LOG) {
      const emoji = value <= getThreshold(metricName) ? '✅' : '⚠️';
      console.log(`[Veluna Web Vitals] ${emoji} ${metricName}: ${value.toFixed(2)}${getUnit(metricName)}`, metadata || '');
    }
  }

  // Helper: Get threshold for metric
  function getThreshold(metricName) {
    const thresholds = {
      lcp: 2500,  // 2.5s (good)
      inp: 200,   // 200ms (good)
      cls: 0.1,   // 0.1 (good)
      fcp: 1800,  // 1.8s (good)
      ttfb: 800   // 800ms (good)
    };
    return thresholds[metricName] || 0;
  }

  // Helper: Get unit for metric
  function getUnit(metricName) {
    if (metricName === 'cls') return '';
    return metricName === 'inp' ? 'ms' : 'ms';
  }

  // LCP (Largest Contentful Paint)
  function observeLCP() {
    if (typeof PerformanceObserver === 'undefined') {
      return;
    }

    try {
      const observer = new PerformanceObserver(function(list) {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        
        metrics.lcp = lastEntry.renderTime || lastEntry.loadTime;
        
        logMetric('LCP', metrics.lcp);
        sendMetric('lcp', metrics.lcp, {
          element: lastEntry.element?.tagName || 'unknown',
          size: lastEntry.size || 0
        });
      });

      observer.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {
      console.warn('[Veluna Web Vitals] LCP observation not supported:', e);
    }
  }

  // INP (Interaction to Next Paint)
  function observeINP() {
    if (typeof PerformanceObserver === 'undefined') {
      return;
    }

    try {
      let interactionCount = 0;
      let maxDelay = 0;

      const observer = new PerformanceObserver(function(list) {
        const entries = list.getEntries();
        
        for (const entry of entries) {
          if (entry.duration > maxDelay) {
            maxDelay = entry.duration;
          }
          interactionCount++;
        }

        // Final INP is the max delay
        if (interactionCount > 0) {
          metrics.inp = maxDelay;
        }
      });

      // Observe event entries (click, keydown, pointerdown)
      observer.observe({ type: 'event', buffered: true });

      // Report on page unload
      window.addEventListener('beforeunload', function() {
        if (metrics.inp !== null) {
          logMetric('INP', metrics.inp);
          sendMetric('inp', metrics.inp);
        }
      });

      // Also report periodically (every 10s) if value changes
      setInterval(function() {
        if (metrics.inp !== null && metrics.inp > 0) {
          logMetric('INP', metrics.inp);
          sendMetric('inp', metrics.inp);
        }
      }, 10000);
    } catch (e) {
      console.warn('[Veluna Web Vitals] INP observation not supported:', e);
    }
  }

  // CLS (Cumulative Layout Shift)
  function observeCLS() {
    if (typeof PerformanceObserver === 'undefined') {
      return;
    }

    try {
      let clsValue = 0;
      let clsEntries = [];

      const observer = new PerformanceObserver(function(list) {
        for (const entry of list.getEntries()) {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
            clsEntries.push(entry);
          }
        }

        metrics.cls = clsValue;
      });

      observer.observe({ type: 'layout-shift', buffered: true });

      // Report on page unload
      window.addEventListener('beforeunload', function() {
        if (metrics.cls !== null) {
          logMetric('CLS', metrics.cls);
          sendMetric('cls', metrics.cls, {
            entries_count: clsEntries.length
          });
        }
      });
    } catch (e) {
      console.warn('[Veluna Web Vitals] CLS observation not supported:', e);
    }
  }

  // FCP (First Contentful Paint)
  function observeFCP() {
    if (typeof PerformanceObserver === 'undefined') {
      return;
    }

    try {
      const observer = new PerformanceObserver(function(list) {
        const entries = list.getEntries();
        for (const entry of entries) {
          if (entry.name === 'first-contentful-paint') {
            metrics.fcp = entry.startTime;
            logMetric('FCP', metrics.fcp);
            sendMetric('fcp', metrics.fcp);
            observer.disconnect();
          }
        }
      });

      observer.observe({ type: 'paint', buffered: true });
    } catch (e) {
      console.warn('[Veluna Web Vitals] FCP observation not supported:', e);
    }
  }

  // TTFB (Time to First Byte)
  function observeTTFB() {
    try {
      const navigationEntry = performance.getEntriesByType('navigation')[0];
      if (navigationEntry && navigationEntry.responseStart) {
        metrics.ttfb = navigationEntry.responseStart - navigationEntry.requestStart;
        logMetric('TTFB', metrics.ttfb);
        sendMetric('ttfb', metrics.ttfb);
      }
    } catch (e) {
      console.warn('[Veluna Web Vitals] TTFB observation not supported:', e);
    }
  }

  // Initialize observations
  function init() {
    // Wait for page to be interactive
    if (document.readyState === 'complete') {
      observeLCP();
      observeINP();
      observeCLS();
      observeFCP();
      observeTTFB();
    } else {
      window.addEventListener('load', function() {
        observeLCP();
        observeINP();
        observeCLS();
        observeFCP();
        observeTTFB();
      });
    }
  }

  // Export metrics for external access
  window.VelunaWebVitals = {
    getMetrics: function() {
      return { ...metrics };
    },
    getMetric: function(name) {
      return metrics[name] || null;
    }
  };

  // Initialize
  init();

})();



